import React from 'react';
import { Link } from 'react-router-dom';

/**
 * CenturionLogoMark - official Centurion Matting wordmark.
 *
 * Two canonical static PNG assets (solid-#000 background - intentional
 * brand "license-plate" look):
 *   • /centurion-logo.png         - 1200×355 (web 1× / favicon source)
 *   • /centurion-logo-hires.png   - 1824×540 (2× / PDF print source)
 *
 * The asset's solid black background is *meant* to merge with surrounding
 * matte-black surfaces (#0B0B0D header, #15171B surfaces). On near-black
 * surfaces the residual delta is imperceptible at small sizes; we therefore
 * render <img> directly with no CSS frame (Option A - direct).
 *
 * Sizing: parent controls height via the `height` prop; width derives from
 * the PNG's natural aspect ratio 1200 : 355  ≈  3.38 : 1.
 *
 * Hover / focus (when `interactive`): the wrapping <a> gains a 2 px lift.
 * Reduced-motion disables the lift.
 */
export const CenturionLogoMark = ({
    height = 56,
    asLink = true,
    interactive = true,
    eager = false,
    testId = 'brand',
}) => {
    // Natural PNG dims of the canonical web asset (1200 × 355).
    const NAT_W = 1200;
    const NAT_H = 355;
    const imgWidth = Math.round(height * (NAT_W / NAT_H));

    const mark = (
        <img
            src="/centurion-logo.png"
            srcSet="/centurion-logo.png 1x, /centurion-logo-hires.png 2x"
            alt="Centurion Matting Products"
            data-testid={testId}
            width={imgWidth}
            height={height}
            className="logo-img block flex-none select-none"
            style={{ height: `${height}px`, width: `${imgWidth}px`, display: 'block' }}
            draggable="false"
            loading={eager ? 'eager' : 'lazy'}
            fetchPriority={eager ? 'high' : 'auto'}
        />
    );

    if (!asLink) return mark;

    return (
        <Link
            to="/"
            aria-label="Centurion Matting Products - Home"
            data-testid="brand-link"
            className={[
                'logo-link group inline-flex items-center outline-none',
                interactive
                    ? 'transition-[transform,filter] duration-200 ease-out motion-reduce:transform-none hover:-translate-y-[2px] focus-visible:-translate-y-[2px]'
                    : '',
            ].join(' ')}
        >
            {mark}
        </Link>
    );
};

export default CenturionLogoMark;
